import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { on, addClass, selectAll } from 'helpers/dom'
import { map } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'

const selectors = {
  CLOSE_SELECTOR: '.js-close-modal-dialog',
  CONTENT_SELECTOR: '.js-content-modal-dialog',
  OPEN_CLASS: 'modal-dialog-opened',
  CLOSE_CLASS: 'modal-dialog-closed',
  OVERLAY_CLASS: 'modal-dialog-overlay',
  VALIDATE_FORM_CLASS: 'validate-form'
}

class ModalDialog extends HTMLElement {
  constructor() {
    super()

    this.init()
  }

  init() {
    this.closeBtn = this.querySelectorAll(selectors.CLOSE_SELECTOR)
    this.contentEl = this.querySelector(selectors.CONTENT_SELECTOR)
    this.isSlideOut = this.dataset && this.dataset.type === 'slideout'
    this.isSlideUp = this.dataset && this.dataset.type === 'slideup'
    this.isModal = this.dataset && this.dataset.type === 'modal'
    this.scrollTarget = this.getAttribute('data-scroll-target')
    this.attachEvents()
    this.addOverlay()
  }

  attachEvents() {
    this.closeBtn.forEach((btn) => on('click', this.hide.bind(this), btn))

    // Track whether the mousedown originated inside the modal content.
    // A drag that starts inside content and ends on the overlay produces a
    // click on the modal's common ancestor — we must not treat that as an
    // overlay close intent.
    on('mousedown', (e) => {
      this._mouseDownOnContent = !!(this.contentEl && this.contentEl.contains(e.target))
    }, this)

    on('click', () => {
      if (this._mouseDownOnContent) return
      this.hide()
    }, this)

    this.contentEl && on('click', (e) => e.stopPropagation(), this.contentEl)
    on(
      'keyup',
      (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') this.hide()
      },
      this
    )
  }

  addOverlay() {
    if ((this.isSlideOut || this.isSlideUp || this.isModal) && !this.overlayEl) {
      this.overlayEl = document.createElement('div')
      addClass(selectors.OVERLAY_CLASS, this.overlayEl)
      this.insertBefore(this.overlayEl, this.contentEl)
    }
  }

  show(target, init = false) {
    init && this.init()
    /** Hide all opened modal before open new modal */
    const allModals = document.querySelectorAll(selectors.OPEN_CLASS)
    allModals.forEach((modal) => modal.hide())

    this.setAttribute('open', '')
    this.classList.add(selectors.OPEN_CLASS)

    /** Lets contents defer expensive work until the modal is actually opened */
    this.dispatchEvent(new CustomEvent('modal:opened', { bubbles: true, detail: { target: this.dataset.target } }))

    // Configure body-scroll-lock to work with iOS Safari
    const scrollLockOptions = {
      reserveScrollBarGap: true,
      allowTouchMove: (el) => {
        // Allow scrolling within the modal content
        const scrollTargets = this.scrollTarget ? selectAll(this.scrollTarget, this) : []

        // Check if the element is within an allowed scroll container
        while (el && el !== document.body) {
          // Allow scrolling in scroll target elements (like .js-mini-cart-content)
          if (scrollTargets.some(target => target.contains(el) || target === el)) {
            return true
          }
          // Allow scrolling in the modal content
          if (el === this.contentEl) {
            return true
          }
          el = el.parentElement
        }
        return false
      }
    }

    if (!target) {
      const scrollTargetEl = selectAll(this.scrollTarget, this)
      scrollTargetEl.forEach(el => disableBodyScroll(el, scrollLockOptions))
      if (this.contentEl) {
        disableBodyScroll(this.contentEl, scrollLockOptions)
      }
    } else {
      disableBodyScroll(target, scrollLockOptions)
    }
  }

  hide() {
    /** Refresh validate form when hide modal */
    const allFormsValidate = this.querySelectorAll(selectors.VALIDATE_FORM_CLASS)
    allFormsValidate.forEach((form) => form.refreshValidate())

    this.classList.add(selectors.CLOSE_CLASS)
    setTimeout(() => {
      this.classList.remove(selectors.CLOSE_CLASS)
    }, 500)
    this.removeAttribute('open')
    this.classList.remove(selectors.OPEN_CLASS)
    clearAllBodyScrollLocks()
    globalEmitter.emit(emitterProps.modal.close, this)
  }
}

customElements.define('modal-dialog', ModalDialog)
